import type { AssignableRole, EffectiveRole } from "@unbox/api";
import {
  canAccessWorkspaceArea as canAccessWorkspaceAreaFor,
  canConfigureSafety as canConfigureSafetyFor,
  getEffectiveMerchantRole,
  invitableRoles as invitableRolesFor,
} from "@unbox/api";
import { useMe } from "@/api/me";
import { useMerchant } from "@/providers/MerchantProvider";

export type Permissions = {
  /** The user's effective role for the brand currently in scope. */
  effectiveRole: EffectiveRole;
  /** Configure individual brand safety risks & policies. */
  canConfigureSafety: boolean;
  /** Access the workspace area / workspace-level configuration. */
  canAccessWorkspaceArea: boolean;
  /** Roles this user may grant to team members. */
  invitableRoles: AssignableRole[];
};

// The exact same resolution logic runs on the backend (middleware) via the shared
// @unbox/api helpers — the frontend never re-derives auth rules.

// Used while `me`/scope is still loading: deny restricted actions so we never
// briefly enable controls the user may not have. Once `me` resolves, the real
// effective role takes over.
const RESTRICTED: Permissions = {
  effectiveRole: "user",
  canConfigureSafety: false,
  canAccessWorkspaceArea: false,
  invitableRoles: ["user"],
};

/** Scoped-role permissions for the brand currently in scope (UNB-1912). */
export const usePermissions = (): Permissions => {
  const { data: me } = useMe();
  const merchantId = useMerchant();

  if (!me || !merchantId) return RESTRICTED;

  const effectiveRole = getEffectiveMerchantRole(me, merchantId);
  return {
    effectiveRole,
    canConfigureSafety: canConfigureSafetyFor(effectiveRole),
    canAccessWorkspaceArea: canAccessWorkspaceAreaFor(effectiveRole),
    invitableRoles: invitableRolesFor(effectiveRole),
  };
};
